import { useEffect, useState } from "react";
import { FlaskConical, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { clearSampleData, fetchSampleDataStatus } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Shown while a tenant still has the demo job created during setup.
 * One tap removes every record stamped `is_sample` — real jobs are
 * never touched.
 */
export default function SampleDataBanner() {
  const { user, isAdmin } = useAuth();
  const [count, setCount] = useState(0);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (!user || !isAdmin) return;
    fetchSampleDataStatus()
      .then((s) => setCount(s.count || 0))
      .catch(() => {});
  }, [user, isAdmin]);

  if (!isAdmin || count < 1) return null;

  const clear = async () => {
    setClearing(true);
    try {
      const r = await clearSampleData();
      setCount(0);
      toast.success(`Cleared ${r.deleted} sample record${r.deleted === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear sample data");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900/50 p-3 flex items-center gap-3"
      data-testid="sample-data-banner"
    >
      <div className="h-9 w-9 rounded-md bg-amber-500 text-white flex items-center justify-center shrink-0">
        <FlaskConical className="h-4.5 w-4.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-slate-900 dark:text-slate-100">
          You're looking at sample data
        </div>
        <div className="text-xs text-slate-600 dark:text-slate-400">
          {count} demo job{count === 1 ? "" : "s"} created during setup so you can click
          around. Clear it whenever you're ready for real work.
        </div>
      </div>
      <button
        onClick={clear}
        disabled={clearing}
        className="text-sm font-semibold px-3 py-2 rounded-md bg-amber-600 hover:bg-amber-500 text-white flex items-center gap-1.5 disabled:opacity-60"
        data-testid="sample-data-clear-btn"
      >
        {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        {clearing ? "Clearing…" : "Clear sample data"}
      </button>
    </div>
  );
}
